import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const Scanner = () => {
    const { config } = useConfig();
    const [scanResult, setScanResult] = useState(null);
    const [isScanning, setIsScanning] = useState(true);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scanMode, setScanMode] = useState('');

    const primaryColor = config?.primaryColor || '#1e40af';

    useEffect(() => {
        let scanner = null;
        
        if (isScanning) {
            scanner = new Html5QrcodeScanner('reader', {
                qrbox: {
                    width: 250,
                    height: 250,
                },
                fps: 5,
            });

            scanner.render(onScanSuccess, onScanError);
        }

        function onScanSuccess(result) {
            scanner.clear();
            setIsScanning(false);
            processScan(result);
        }

        function onScanError(err) {
            // console.warn(err);
        }

        return () => {
            if (scanner) {
                scanner.clear().catch(e => console.error("Scanner cleanup error", e));
            }
        };
    }, [isScanning]);

    const processScan = async (userId) => {
        setLoading(true);
        setError(null);
        try {
            const payload = { userId };
            if (scanMode) {
                payload.type = scanMode;
            }
            const response = await api.post('/attendance/scan', payload);
            setScanResult(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al procesar el escaneo');
        } finally {
            setLoading(false);
        }
    };

    const handleScannerReset = () => {
        setScanResult(null);
        setError(null);
        setIsScanning(true);
    };

    return (
        <div className="animate-fadeIn min-h-[60vh] flex flex-col items-center">
            <header className="mb-8 w-full">
                <h1 className="text-3xl font-black text-white tracking-tight italic uppercase tracking-tighter">Escáner de Asistencia</h1>
                <p className="text-white/60 font-medium mt-1 uppercase tracking-widest text-[10px]">Registra entrada y salida de asistentes.</p>
            </header>

            <div className="w-full max-w-md bg-white/10 backdrop-blur-2xl rounded-[50px] border border-white/20 shadow-2xl overflow-hidden p-8 sm:p-10">
                {isScanning && (
                    <div className="flex justify-center gap-2 mb-6">
                        <button 
                            onClick={() => setScanMode('')}
                            className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === '' ? 'bg-white text-[#1c0042]' : 'bg-white/10 text-white hover:bg-white/20'}`}
                        >
                            Auto
                        </button>
                        <button 
                            onClick={() => setScanMode('entry')}
                            className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === 'entry' ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
                        >
                            Ingreso
                        </button>
                        <button 
                            onClick={() => setScanMode('exit')}
                            className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${scanMode === 'exit' ? 'bg-orange-500 text-white' : 'bg-white/10 text-white hover:bg-white/20'}`}
                        >
                            Salida
                        </button>
                    </div>
                )}
                {isScanning ? (
                    <div className="relative">
                        <div id="reader" className="overflow-hidden rounded-3xl border-none shadow-2xl bg-black/20"></div>
                        <div className="mt-10 text-center">
                            <div className="inline-block px-6 py-3 bg-white/10 rounded-full text-[10px] font-black uppercase tracking-[0.3em] text-white border border-white/10 animate-pulse">
                                Buscando código QR...
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="py-8 flex flex-col items-center text-center">
                        {loading ? (
                            <div className="flex flex-col items-center py-10">
                                <div className="w-12 h-12 border-4 border-white/10 border-t-white rounded-full animate-spin"></div>
                                <p className="mt-6 font-black text-[10px] uppercase tracking-[0.4em] text-white/40">Procesando...</p>
                            </div>
                        ) : scanResult ? (
                            <div className="space-y-8 animate-scaleUp">
                                <div 
                                    className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 ${
                                        scanResult.type === 'entry' ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'bg-orange-500/20 border-orange-500 text-orange-400'
                                    }`}
                                >
                                    {scanResult.type === 'entry' ? '✓' : '👋'}
                                </div>
                                <div>
                                    <h2 className="text-3xl font-black text-white leading-tight uppercase italic tracking-tighter">
                                        {scanResult.user.name} <br/> {scanResult.user.last_name}
                                    </h2>
                                    <p className={`font-black uppercase tracking-[0.3em] text-[11px] mt-4 py-2 px-4 rounded-full inline-block ${
                                        scanResult.type === 'entry' ? 'bg-emerald-500 text-white' : 'bg-orange-500 text-white'
                                    }`}>
                                        {scanResult.type === 'entry' ? 'Entrada Registrada' : 'Salida Registrada'}
                                    </p>
                                    
                                    {scanResult.sessionTitle && (
                                        <p className="text-white/80 text-[11px] mt-4 font-bold uppercase tracking-wider bg-white/10 py-2 px-4 rounded-xl border border-white/10">
                                            Charla: {scanResult.sessionTitle}
                                        </p>
                                    )}
                                    
                                    {scanResult.timestamp && (
                                        <p className="text-white/60 text-[10px] mt-2 font-medium uppercase tracking-widest">
                                            {new Date(scanResult.timestamp).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                                        </p>
                                    )}
                                    
                                    <p className="text-white/30 text-[10px] mt-6 font-black uppercase tracking-widest">Identificación: {scanResult.user.identification}</p>
                                </div>
                                <button 
                                    onClick={handleScannerReset}
                                    className="w-full py-5 bg-white text-[#1c0042] rounded-3xl font-black uppercase tracking-[0.2em] text-[11px] hover:scale-105 active:scale-95 transition-all shadow-2xl"
                                >
                                    Escanear Siguiente
                                </button>
                            </div>
                        ) : error ? (
                            <div className="space-y-8 animate-shake">
                                <div className="w-24 h-24 mx-auto bg-red-500/20 border-4 border-red-500 text-red-400 rounded-full flex items-center justify-center text-4xl shadow-2xl">
                                    ✕
                                </div>
                                <div>
                                    <h2 className="text-2xl font-black text-white uppercase italic">Error en Escaneo</h2>
                                    <p className="text-red-400 font-bold mt-4 uppercase tracking-tight text-xs bg-red-500/10 p-4 rounded-2xl border border-red-500/20">{error}</p>
                                </div>
                                <button 
                                    onClick={handleScannerReset}
                                    className="w-full py-5 bg-white/10 text-white border border-white/20 rounded-3xl font-black uppercase tracking-[0.2em] text-[11px] hover:bg-white/20 transition-all"
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
            
            <div className="mt-12 flex flex-col items-center gap-6">
                <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.5em] italic">Staff Only Control</p>
                <div className="flex gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-white/10"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse"></div>
                    <div className="w-1.5 h-1.5 rounded-full bg-white/10"></div>
                </div>
            </div>
        </div>
    );
};

export default Scanner;
